import { SalaryType } from "@me-team/host/main/graphql/types";

export interface SalarySection {
    id: number;
    categoryId?: number;
    subCategoryId?: number;
    salary: number;
    salaryType: string;
    categoryName?: string;
    subCategoryName?: string;
    selectedAllCategories?: boolean;
    update?: boolean;
    errorCategory?: boolean;
    salaryError?: boolean;
    errorMessage?: string;
    mutation?: string;
    currSign?: string;
    hasInteracted?: boolean;
    salaryErrorMessage?: string;
    categoryErrorMessage?: string;
}

interface Employee {
    id: number;
    ServiceCategorySalaries?: ServiceCategorySalaryProxy[];
    name?: string;
    surname?: string;
}

interface ServiceCategorySalaryProxy {
    id: number;
    salaryValue: number;
    salaryType: SalaryType;
    serviceCategory: { id: number; name: string }[];
    selectedAll: boolean;
    deleted: boolean;
}

export interface Salary {
    categoryId: number;
    subCategoryId: number;
    salary: string;
    salaryType: string;
    categoryName: string;
}

export interface FormValues {
    companyId: number;
    salarySections: Salary[];
}

export const validateSalarySections = async (
    salarySections: SalarySection[],
    checkbox: boolean,
    t: (key: string) => string,
    isSubmitting: boolean = false,
    getServicesPrice: (options: { variables: { companyId: number; categoryId?: number } }) => Promise<any>,
    companyId: number
): Promise<{ hasErrors: boolean; updatedSections: SalarySection[] }> => {
    let hasErrors = false;

    const updatedSections = await Promise.all(
        salarySections.map(async (section) => {
            const updated = {
                ...section,
                errorCategory: false,
                salaryError: false,
                errorMessage: "",
                salaryErrorMessage: "",
                categoryErrorMessage: "",
            };

            const { categoryId, subCategoryId, salaryType, salary, hasInteracted } = updated;

            const shouldValidateSalary = isSubmitting || hasInteracted;

            if (!checkbox && !categoryId && !subCategoryId) {
                hasErrors = true;
                updated.errorCategory = true;
                updated.categoryErrorMessage = t("Fill this field");
            }

            if (shouldValidateSalary) {
                const isPercent = salaryType === "%";
                const isFixed = !isPercent;

                if (isPercent) {
                    if (!(salary > 0 && salary <= 100)) {
                        hasErrors = true;
                        updated.salaryError = true;
                        updated.salaryErrorMessage =
                            salary > 100 ? t("Enter a value between 0 and 100.") : t("Fill this field");
                    }
                } else if (isFixed) {
                    if (!(salary > 0)) {
                        hasErrors = true;
                        updated.salaryError = true;
                        updated.salaryErrorMessage = t("Fill this field");
                    } else {
                        try {
                            const { data } =
                                companyId &&
                                (await getServicesPrice({
                                    variables: {
                                        companyId,
                                        categoryId: checkbox ? undefined : ((subCategoryId || categoryId) ?? undefined),
                                    },
                                }));

                            const services = data?.user?.company?.servicesList ?? [];
                            const prices = services.map((s: any) => +s.price || 0).filter((price: number) => price > 0);

                            const minPrice = Math.min(...prices);

                            if (salary > minPrice) {
                                hasErrors = true;
                                updated.salaryError = true;
                                updated.salaryErrorMessage = t("Salary cannot be more than service price");
                            }
                        } catch (e) {
                            console.error("getServicesPrice error:", e);
                        }
                    }
                }
            }

            return updated;
        })
    );

    const seen = new Map<string, number[]>();
    updatedSections.forEach((section, index) => {
        const key = `${section.categoryId || "null"}-${section.subCategoryId || "null"}`;
        if (seen.has(key)) {
            hasErrors = true;
            section.errorCategory = true;
            section.errorMessage = t("Categories/Subcategories should not be repeated");
            seen.get(key)!.forEach((i) => {
                updatedSections[i].errorCategory = true;
                updatedSections[i].errorMessage = t("Categories/Subcategories should not be repeated");
            });
            seen.get(key)!.push(index);
        } else {
            seen.set(key, [index]);
        }
    });

    return { hasErrors, updatedSections };
};

export const processDeleteState = async (
    deleteState: SalarySection[],
    updateServiceCategory: any,
    setDeleteState: React.Dispatch<React.SetStateAction<SalarySection[]>>,
    branchId: string,
    companyId: string,
    currentBranchEmployeeId?: number
) => {
    if (deleteState.length > 0) {
        const deletePromises = deleteState.map(async (section) => {
            return updateServiceCategory({
                variables: {
                    serviceCategorySalaryId: section.id,
                    input: {
                        salaryValue: section.salary,
                        salaryType: section.salaryType === "%" ? SalaryType.Percent : SalaryType.Currency,
                        selectedAllCategories: section.selectedAllCategories,
                        deleted: true,
                    },
                    branchId: +branchId,
                    companyId: +companyId,
                    employeeId: Number(currentBranchEmployeeId),
                },
            });
        });
        await Promise.allSettled(deletePromises);
        setDeleteState([]);
    }
};

export const processSalarySection = async (
    section: SalarySection,
    checkbox: boolean,
    employee: Employee,
    currentCompanyId: string,
    branchId: string,
    currentBranchEmployeeId: number | undefined,
    updateServiceCategory: any,
    createServiceCategory: any
) => {
    const salaryValue = Number(parseFloat(section.salary.toString()).toFixed(2));
    const salaryType = section.salaryType === "%" ? SalaryType.Percent : SalaryType.Currency;
    let categoryNum = Number(section.subCategoryId) || Number(section.categoryId);
    let mutation;
    let variables;

    if (section.update) {
        mutation = updateServiceCategory;
        variables = {
            serviceCategorySalaryId: section.id,
            companyId: +currentCompanyId,
            input: {
                serviceCategoryId: categoryNum,
                salaryValue: salaryValue,
                salaryType: salaryType,
                selectedAllCategories: checkbox,
            },
            branchId: +branchId,
            employeeId: Number(currentBranchEmployeeId),
        };
    } else {
        const existingSalary = employee?.ServiceCategorySalaries?.find(
            (salary) =>
                salary.serviceCategory.length > 0 &&
                salary.serviceCategory[0].id === categoryNum &&
                salary.deleted === true
        );

        if (existingSalary) {
            if (checkbox) {
                categoryNum = existingSalary.serviceCategory[0].id;
            }
            mutation = updateServiceCategory;
            variables = {
                serviceCategorySalaryId: existingSalary.id,
                companyId: +currentCompanyId,
                branchId: +branchId,
                employeeId: Number(currentBranchEmployeeId),
                input: {
                    serviceCategoryId: categoryNum,
                    salaryValue: salaryValue,
                    salaryType: salaryType,
                    selectedAllCategories: checkbox,
                    deleted: false,
                },
            };
        } else {
            mutation = createServiceCategory;
            variables = {
                employeeId: employee?.id,
                companyId: +currentCompanyId,
                branchId: +branchId,
                input: {
                    serviceCategoryId: categoryNum,
                    salaryValue: salaryValue,
                    salaryType: salaryType,
                    selectedAllCategories: checkbox,
                },
            };
        }
    }

    return mutation({
        variables,
    });
};

export const transformSalarySections = (
    employee: Employee,
    defaultSalarySection: SalarySection,
    currSign: string
): SalarySection[] => {
    const transformedSections = employee.ServiceCategorySalaries.reduce(
        (accumulator: SalarySection[], item: ServiceCategorySalaryProxy) => {
            if (!item.deleted) {
                accumulator.push({
                    id: item.id,
                    categoryId: item.serviceCategory[0]?.id || null,
                    salary: item.salaryValue,
                    salaryType: item.salaryType === 1 ? "%" : currSign,
                    categoryName: item.serviceCategory[0]?.name || "",
                    subCategoryId: null,
                    selectedAllCategories: item.selectedAll,
                    update: true,
                });
            }
            return accumulator;
        },
        [] as SalarySection[]
    );

    if (transformedSections.length === 0) transformedSections.push(defaultSalarySection);
    return transformedSections;
};
